import { Alert, Badge, Card, Grid, Group, NavLink, ScrollArea, Stack, Switch, Table, Text, Tooltip } from '@mantine/core';
import { IconInfoCircle, IconLock } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  PERMISSION_ACTIONS,
  PHASE_TWO_MODULES,
  type AccessMatrixDto,
  type AppModule,
  type CeilingSet,
  type MatrixRowDto,
  type PermissionAction,
  type Role,
} from '@mexp/shared';
import { http } from '@/api/client';
import { useAuth, useCurrentUser } from '@/auth/AuthProvider';
import { PageHeader } from '@/components/PageHeader';
import { ErrorState, LoadingState } from '@/components/States';
import { fromNow } from '@/lib/format';
import { notifyError } from '@/lib/notify';

const ACTION_LABELS: Record<PermissionAction, string> = { view: 'View', edit: 'Create & edit', delete: 'Delete', export: 'Export' };
const SOURCE_LABELS: Record<string, string> = { default: 'Default', legacy: 'From old system', manual: 'Changed here' };

function allowed(set: CeilingSet, role: Role): boolean {
  if (set === 'all') return true;
  if (set === 'staff') return role !== 'exporter';
  return set.includes(role);
}

export function AccessControlPage() {
  const me = useCurrentUser();
  const { can, reloadPermissions } = useAuth();
  const queryClient = useQueryClient();
  const [role, setRole] = useState<Role>('ra');
  const matrix = useQuery({ queryKey: ['access-control'], queryFn: () => http.get<AccessMatrixDto>('/access-control/matrix') });

  const update = useMutation({
    mutationFn: (row: MatrixRowDto) =>
      http.put<MatrixRowDto>(`/access-control/${row.role}/${row.module}`, { view: row.view, edit: row.edit, delete: row.delete, export: row.export }),
    onMutate: async (row) => {
      await queryClient.cancelQueries({ queryKey: ['access-control'] });
      const previous = queryClient.getQueryData<AccessMatrixDto>(['access-control']);
      queryClient.setQueryData<AccessMatrixDto>(['access-control'], (old) =>
        old ? { ...old, rows: old.rows.map((r) => (r.role === row.role && r.module === row.module ? row : r)) } : old,
      );
      return { previous };
    },
    onError: (error, _row, context) => {
      if (context?.previous) queryClient.setQueryData(['access-control'], context.previous);
      notifyError(error, 'Permission not saved');
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<AccessMatrixDto>(['access-control'], (old) =>
        old ? { ...old, rows: old.rows.map((r) => (r.role === saved.role && r.module === saved.module ? saved : r)) } : old,
      );
      if (saved.role === me.role) void reloadPermissions();
    },
  });

  if (matrix.isLoading) return <LoadingState />;
  if (matrix.isError || !matrix.data) return <ErrorState error={matrix.error} onRetry={() => void matrix.refetch()} />;
  const data = matrix.data;
  const editable = can('AccessControl', 'edit');
  const rows = data.rows.filter((r) => r.role === role);
  const lockedRole = role === 'super_admin' || (role === 'admin' && me.role !== 'super_admin');

  const toggle = (row: MatrixRowDto, action: PermissionAction, value: boolean) => {
    const next = { ...row, [action]: value };
    // Anything beyond "view" needs "view"; switching view off switches the rest off.
    if (action === 'view' && !value) {
      next.edit = false;
      next.delete = false;
      next.export = false;
    }
    if (action !== 'view' && value) next.view = true;
    update.mutate(next);
  };

  return (
    <>
      <PageHeader
        title="Access control"
        description="What each role can see and do. Changes apply the next time a page loads; the server enforces them on every request."
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card p="xs">
            <ScrollArea.Autosize mah={{ base: 240, md: 'calc(100dvh - 220px)' } as unknown as number}>
              {data.roles.map((r) => (
                <NavLink
                  key={r.role}
                  label={r.label}
                  active={r.role === role}
                  onClick={() => setRole(r.role)}
                  rightSection={r.role === 'super_admin' ? <IconLock size={14} /> : undefined}
                  style={{ borderRadius: 'var(--mantine-radius-md)' }}
                />
              ))}
            </ScrollArea.Autosize>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9 }}>
          <Card p={0}>
            <Stack gap="sm" p="md">
              <Group justify="space-between">
                <Text fw={600}>{data.roles.find((r) => r.role === role)?.label}</Text>
              </Group>
              {lockedRole && (
                <Alert variant="light" color="gray" icon={<IconInfoCircle size={18} />}>
                  {role === 'super_admin' ? 'Super Admin always has full access.' : 'Only a Super Admin can change Admin permissions.'}
                </Alert>
              )}
            </Stack>
            <div className="table-scroll">
              <Table miw={640}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Area</Table.Th>
                    {PERMISSION_ACTIONS.map((a) => (
                      <Table.Th key={a} ta="center">
                        {ACTION_LABELS[a]}
                      </Table.Th>
                    ))}
                    <Table.Th>Source</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((row) => {
                    const label = data.modules.find((m) => m.module === row.module)?.label ?? row.module;
                    const later = (PHASE_TWO_MODULES as readonly AppModule[]).includes(row.module);
                    return (
                      <Table.Tr key={row.module}>
                        <Table.Td>
                          <Group gap={6}>
                            <Text fz="sm">{label}</Text>
                            {later && (
                              <Badge size="xs" variant="outline" color="gray">
                                Phase 2
                              </Badge>
                            )}
                          </Group>
                        </Table.Td>
                        {PERMISSION_ACTIONS.map((action) => {
                          const possible = allowed(data.ceilings[row.module][action], role);
                          const control = (
                            <Switch
                              checked={row[action]}
                              disabled={!editable || lockedRole || !possible}
                              onChange={(e) => toggle(row, action, e.currentTarget.checked)}
                              aria-label={`${label}: ${ACTION_LABELS[action]}`}
                              styles={{ root: { display: 'inline-block' } }}
                            />
                          );
                          return (
                            <Table.Td key={action} ta="center">
                              {possible ? control : <Tooltip label="Not available for this role">{<span>{control}</span>}</Tooltip>}
                            </Table.Td>
                          );
                        })}
                        <Table.Td>
                          <Text fz="xs" c="dimmed" className="nowrap">
                            {SOURCE_LABELS[row.source] ?? row.source}
                            {row.source === 'manual' && row.updatedAt ? ` · ${fromNow(row.updatedAt)}` : ''}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </div>
          </Card>
        </Grid.Col>
      </Grid>
    </>
  );
}
